import * as io from "io-ts";
import { nullable, uuid } from "diagnostics-lib/io";
import { Reparability } from "../../../api/Reparability";
import { MetrologyStatus } from "diagnostics-domain/quantity/metrology";
const FailureValueStatus = io.keyof({
  CONFIRMED: null,
  SUSPECTED: null,
  REJECTED: null,
  LEGACY_REJECTED: null,
  TO_BE_ANALYSED: null,
  BACK_TO_BE_ANALYSED: null,
});
export type FailureValueStatus = io.TypeOf<typeof FailureValueStatus>;
export type Status = FailureValueStatus | Reparability;

const Human = io.strict({
  value: io.number,
  unit: nullable(io.string),
  precision: nullable(io.number),
  sign: nullable(io.boolean),
});

export const ANALYSIS_FLAG_METADATA = "ANALYSIS_FLAG_METADATA";
const AnalysisFlag = io.keyof({
  outdated: null,
  uncompletedSimulation: null,
  uncompletedDiagnosis: null,
  unavailableSimulation: null,
  publishedAndIncludedAnalysis: null,
  publishedNotIncludedAnalysis: null,
  simulationError: null,
  diagnosisError: null,
  allData: null,
});

export type AnalysisFlag = io.TypeOf<typeof AnalysisFlag>;
export const AnalysisFlagMetadata = io.strict({
  type: io.literal(ANALYSIS_FLAG_METADATA),
  human: nullable(Human),
  group: nullable(io.string),
  label: nullable(io.string),
  flagType: AnalysisFlag,
});
export type AnalysisFlagMetadata = io.TypeOf<typeof AnalysisFlagMetadata>;

const AllDataFlag = io.keyof({
  allData: null,
});
export type AllDataFlag = io.TypeOf<typeof AllDataFlag>;
export const AllDataFlagMetadata = io.strict({
  type: io.literal(ANALYSIS_FLAG_METADATA),
  human: nullable(Human),
  group: nullable(io.string),
  label: nullable(io.string),
  flagType: AllDataFlag,
});
export type AllDataFlagMetadata = io.TypeOf<typeof AllDataFlagMetadata>;

export const MEASURE_POINT_METADATA = "MEASURE_POINT_METADATA";
export const MeasurePointMetadata = io.intersection([
  io.strict({
    type: io.literal(MEASURE_POINT_METADATA),
    human: nullable(Human),
    group: nullable(io.string),
    label: nullable(io.string),
    analysisId: io.string,
    date: io.number,
    validity: MetrologyStatus,
    publishedAnalysis: io.boolean,
    quantityId: io.string,
    quantityName: io.string,
  }),
  io.partial({ active: io.boolean }),
]);
export type MeasurePointMetadata = io.TypeOf<typeof MeasurePointMetadata>;

export const GAP_POINT_METADATA = "GAP_POINT_METADATA";
export const GapPointMetadata = io.strict({
  type: io.literal(GAP_POINT_METADATA),
  human: nullable(Human),
  group: nullable(io.string),
  label: nullable(io.string),
  isSymptom: nullable(io.boolean),
});
export type GapPointMetadata = io.TypeOf<typeof GapPointMetadata>;

export const QuantityFlag = io.keyof({
  missingValue: null,
  noModelOutput: null,
  invalidValue: null,
});
export type QuantityFlag = io.TypeOf<typeof QuantityFlag>;
export const QuantityFlagItem = io.strict({
  name: io.string,
  type: QuantityFlag,
  description: io.string,
  origin: io.union([io.literal("expected"), io.literal("measured")]),
});
export type QuantityFlagItem = io.TypeOf<typeof QuantityFlagItem>;
export const QUANTITY_FLAG_METADATA = "QUANTITY_FLAG_METADATA";
export const QuantityFlagMetadata = io.strict({
  type: io.literal(QUANTITY_FLAG_METADATA),
  human: nullable(Human),
  group: nullable(io.string),
  label: nullable(io.string),
  flags: io.array(QuantityFlagItem),
});
export type QuantityFlagMetadata = io.TypeOf<typeof QuantityFlagMetadata>;

export const NO_FAILURE_METADATA = "NO_FAILURE_METADATA";
export const NoFailureMetadata = io.strict({
  type: io.literal(NO_FAILURE_METADATA),
  group: nullable(io.string),
  label: nullable(io.string),
  analysisId: io.string,
});
export type NoFailureMetadata = io.TypeOf<typeof NoFailureMetadata>;

export const Filters = io.strict({
  id: uuid,
  type: io.literal("MEAN"),
  quantityId: io.string,
  min: nullable(io.number),
  max: nullable(io.number),
  rule: io.union([io.literal("CONSTRAINT"), io.literal("PROMOTION")]),
});
export type Filters = io.TypeOf<typeof Filters>;

export const QuantityValue = io.strict({
  measured: io.number,
  simulated: nullable(io.number),
  std: nullable(io.number),
});

export const FAILURE_METADATA = "FAILURE_METADATA";
export const FailureMetadata = io.strict({
  type: io.literal(FAILURE_METADATA),
  group: nullable(io.string),
  label: nullable(io.string),
  analysisId: io.string,
  analysisDate: io.number,
  failureName: nullable(io.string),
  failureDisplayName: io.string,
  human: nullable(Human),
  confidenceIndicator: io.union([nullable(io.number), io.union([io.undefined, io.number])]),
  filters: nullable(io.array(Filters)),
  reparability: nullable(Reparability),
  failureStatus: nullable(FailureValueStatus),
  failureAmplitude: nullable(io.number),
  failureUnit: nullable(io.string),
  failureDescription: nullable(io.string),
  quantityValues: nullable(io.record(io.string, QuantityValue)),
});
export type FailureMetadata = io.TypeOf<typeof FailureMetadata>;

export const MANUAL_FAILURE_METADATA = "MANUAL_FAILURE_METADATA";
export const ManualFailureMetadata = io.strict({
  type: io.literal(MANUAL_FAILURE_METADATA),
  group: nullable(io.string),
  label: nullable(io.string),
  analysisId: io.string,
  human: nullable(Human),
  failureName: nullable(io.string),
  isManual: io.boolean,
  reparability: nullable(Reparability),
});

export type ManualFailureMetadata = io.TypeOf<typeof ManualFailureMetadata>;

export const FailureImpactMetadata = io.strict({
  human: io.union([Human, io.string]),
  failureStatus: io.union([io.undefined, io.union([FailureValueStatus, io.null])]),
  confidenceIndicator: io.union([nullable(io.number), io.union([io.undefined, io.number])]),
  isManual: io.union([io.undefined, io.union([io.null, io.boolean])]),
  isPromoted: io.union([io.undefined, io.boolean, io.null]),
});
export type FailureImpactMetadata = io.TypeOf<typeof FailureImpactMetadata>;

export const ManualFailureImpactMetadata = io.strict({
  human: io.union([Human, io.string]),
});
export type ManualFailureImpactMetadata = io.TypeOf<typeof ManualFailureImpactMetadata>;

export const REPORTING_POINT_METADATA = "REPORTING_POINT_METADATA";
export const ReportingPointMetadata = io.strict({
  type: io.literal("REPORTING_POINT_METADATA"),
  projectCount: io.number,
  filledCount: io.union([io.undefined, io.number]),
});
export type ReportingPointMetadata = io.TypeOf<typeof ReportingPointMetadata>;

export const PointMetadata = io.intersection([
  io.union([
    AnalysisFlagMetadata,
    AllDataFlagMetadata,
    QuantityFlagMetadata,
    MeasurePointMetadata,
    GapPointMetadata,
    FailureMetadata,
    NoFailureMetadata,
    ManualFailureMetadata,
  ]),
  io.partial({
    active: io.boolean,
  }),
]);

export type PointMetadata = io.TypeOf<typeof PointMetadata>;

declare module "highcharts" {
  interface Point {
    custom: PointMetadata;
    graphic?: Highcharts.SVGElement;
  }
}
